import React, { Component } from 'react';
import { array, bool, func, object, shape, string, arrayOf } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { useIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
  moveSecondFilterToEnd,
  constructQueryParamName,
} from '../../util/search';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { findListingFieldByKey } from '../../util/listingHelpers';
import { getListingsById, getUsersById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { addToCart } from '../CartPage/CartPage.duck';

import { NamedRedirect, Page, SecondaryButton } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import TopbarNavigation from '../TopbarContainer/Topbar/TopbarNavigation/TopbarNavigation';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
  getFilteredListingIds,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';

import { isSupabaseAuth } from '../../util/supabaseAuth';

import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

const normalize = s => String(s).trim().toLowerCase();
const toSlug = s => normalize(s).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

// Builds the brand filter's option list FROM availableBrands, rather than
// filtering the hosted config's fixed enum down to it. The config enum is a
// small curated list (e.g. ~53 entries); most of the catalog's real brand
// diversity (scraped into Supabase, which holds most of this marketplace's
// products) was never added to it, so filtering the enum down to only its
// own members discarded almost everything Supabase actually has — some
// categories have zero enum-registered brands at all and showed "no
// results" even though real products with real brands exist there.
//
// For each available brand: reuse the enum's entry (nicer canonical
// label/value, and the value real Sharetribe listings already store) when
// one matches — matching by slug (e.g. "Bond It" -> 'bond-it') or plain
// lowercase, since real listings store the enum's lowercase slug while
// catalog materials store a human-readable label with no relation to it.
// Otherwise synthesize an option straight from the real brand string, so
// e.g. "Hager"/"Schüco"/"Hilti" (present in Supabase, absent from the enum)
// still show up and remain selectable — `pub_brand=<value>` filters both
// Sharetribe (exact match; harmlessly finds nothing if no listing uses it)
// and the catalog (case-insensitive match, see applyBrandFilter server-side)
// the same way regardless of whether the value came from the enum.
const buildBrandOptions = (enumOptions, availableBrands) => {
  // Supabase listings store the brand's name as is (public_data.brand = "Bond It")
  // and the filter matches it exactly, so the option value must be that name. The
  // config enum's slug ('bond-it') matched nothing there.
  const bySlug = isSupabaseAuth() ? new Map() : new Map(enumOptions.map(o => [normalize(o.option), o]));

  const seen = new Set();
  const options = [];
  availableBrands.forEach(raw => {
    const matched = bySlug.get(normalize(raw)) || bySlug.get(toSlug(raw));
    const option = matched || { option: raw, label: raw };
    const dedupeKey = normalize(option.option);
    if (seen.has(dedupeKey)) {
      return;
    }
    seen.add(dedupeKey);
    options.push(option);
  });
  return options.sort((a, b) => a.label.localeCompare(b.label));
};

// Widens the brand field's enumOptions (via buildBrandOptions) within a
// listing-fields array, leaving every other field untouched. Shared by
// render() (category-scoped fields, for what's actually displayed) and
// getEnrichedConfig() (the raw config, for validation elsewhere) — see
// getEnrichedConfig's comment for why both need this applied independently.
const withWidenedBrandOptions = (listingFieldsConfig, availableBrands) => {
  const brandField = findListingFieldByKey(listingFieldsConfig, 'brand');
  if (!availableBrands || !brandField) {
    return listingFieldsConfig;
  }
  return listingFieldsConfig.map(fieldConfig =>
    fieldConfig.key === brandField.key
      ? { ...fieldConfig, enumOptions: buildBrandOptions(fieldConfig.enumOptions || [], availableBrands) }
      : fieldConfig
  );
};

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);

    this.getEnrichedConfig = this.getEnrichedConfig.bind(this);
    this.getEnrichedProps = this.getEnrichedProps.bind(this);
  }

  // validUrlQueryParamsFromProps/initialValues (in SearchPage.shared.js) validate
  // pub_brand against config.listing.listingFields's brand field enumOptions,
  // dropping any value that isn't in that fixed, hosted-config list — so a real
  // brand narrowed in from availableBrands (see buildBrandOptions above; most of
  // this marketplace's brands live in Supabase and were never added to that
  // config) would submit into the URL fine but then get silently treated as
  // invalid on every subsequent read, permanently unable to render as checked.
  // Returns config with the brand field's enumOptions extended the same way
  // render() does, so every consumer of props.config sees the same widened set.
  getEnrichedConfig() {
    const { config, availableBrands } = this.props;
    const { listingFields } = config?.listing || {};
    if (!listingFields) {
      return config;
    }
    const enrichedListingFields = withWidenedBrandOptions(listingFields, availableBrands);
    return enrichedListingFields === listingFields
      ? config
      : { ...config, listing: { ...config.listing, listingFields: enrichedListingFields } };
  }

  getEnrichedProps() {
    return { ...this.props, config: this.getEnrichedConfig() };
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params. The location filter keeps its state in address/bounds
    // and a precomputed `ids` list rather than in a filter param, and a page number
    // past the end of the unfiltered results is meaningless: drop those too, or
    // "Reset filters" left the results narrowed to the old delivery-area ids.
    const queryParams = omit(urlQueryParams, [
      ...filterQueryParamNames,
      'ids',
      'address',
      'bounds',
      'origin',
      'page',
    ]);
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  getHandleChangedValueFn(useHistoryPush) {
    const enrichedProps = this.getEnrichedProps();
    const { history, routeConfiguration, config, marketplaceListings } = enrichedProps;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(enrichedProps);

    // The brand filter's own OPTION LIST is narrowed per category (see
    // buildBrandOptions in render()), so a brand selected under one category
    // can be invalid/meaningless once the category changes — its checkbox
    // may not even be shown anymore, yet the filter would keep silently
    // applying. Whenever this change came from the category filter itself,
    // also drop any current brand selection instead of carrying it over.
    const categoryFilterConfig = defaultFiltersConfig.find(f => f.schemaType === 'category');
    const categoryParamPrefix = categoryFilterConfig
      ? constructQueryParamName(categoryFilterConfig.key, categoryFilterConfig.scope)
      : null;
    const brandField = findListingFieldByKey(listingFieldsConfig, 'brand');
    const brandParamName = brandField
      ? constructQueryParamName(brandField.key, brandField.scope)
      : null;

    return updatedURLParams => {
      const isCategoryChange =
        categoryParamPrefix != null &&
        Object.keys(updatedURLParams).some(k => k.startsWith(categoryParamPrefix));

      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Sharetribe: the Location filter narrows the results to precomputed ids
        // of listings fetched in the browser. On Supabase the listings query
        // applies it from address/origin (see deliveryOriginFromLocationFilter),
        // and a stale `ids` from an old URL is dropped.
        const filteredIds = isSupabaseAuth()
          ? null
          : getFilteredListingIds(updatedURLParams, urlQueryParams, marketplaceListings);

        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        const withLocation = {
          address,
          bounds,
          ...mergedQueryParams,
          ...updatedURLParams,
          ...keywordsMaybe,
          ...(filteredIds || filteredIds === '' ? { ids: filteredIds } : {}),
        };
        const combined = omitLimitedListingFieldParams(
          isSupabaseAuth() ? omit(withLocation, ['ids']) : withLocation,
          filterConfigs
        );
        const currentQueryParams =
          isCategoryChange && brandParamName ? omit(combined, [brandParamName]) : combined;

        return { currentQueryParams };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);
          history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.getEnrichedProps());

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings,
      location,
      onManageDisableScrolling,
      onAddToCart,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams,
      users,
      routeConfiguration,
      config,
      availableBrands,
    } = this.props;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const rawListingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
    });
    // While a category filter is active, availableBrands (state.SearchPage,
    // computed in SearchPage.duck.js's loadData) holds the distinct brands
    // that actually have matching products for that category across both
    // listing sources — narrow the brand filter's option list to those
    // instead of showing every brand from the hosted config. null means "no
    // category filter active (or not yet computed)" — show the full list.
    const listingFieldsConfig = withWidenedBrandOptions(rawListingFieldsConfig, availableBrands);
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      isOriginInUse(config)
    );
    const validQueryParams = urlQueryParams;

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel'].includes(f.key)
    );
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(f => !['keywords', 'categoryLevel'].includes(f.key))
      : defaultFiltersConfig.filter(f => !['categoryLevel'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const availableFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ];
    const sortedAvailableFilters = moveSecondFilterToEnd(availableFilters);

    const filterUsersByListings = (users, listings) => {
      return users.filter(user =>
        // Catalog-sourced listings (Supabase materials) have no author.
        listings.some(listing => listing.author?.id.uuid === user.id.uuid)
      );
    };

    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
          ? listings.length
          : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        resultsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        searchListingsError={searchListingsError}
        isValidDatesFilter={isValidDatesFilter}
        location={location}
        resetAll={this.resetAll}
      />
    );

    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        <TopbarNavigation />
        <div className={css.layoutWrapperContainer}>
          <MainPanelHeader
            className={css.mainPanel}
            listings={listings}
            listingCategories={listingCategories}
            urlQueryParams={validQueryParams}
            getHandleChangedValueFn={this.getHandleChangedValueFn}
            sortByComponent={sortBy('desktop')}
            isSortByActive={sortConfig.active}
          />
          <div className={css.layoutWrapperContent}>
            <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
              <div className={css.filterColumnContent}>
                {sortedAvailableFilters.map(filterConfig => {
                  const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${filterConfig.key
                    }`;
                  return (
                    <FilterComponent
                      key={key}
                      idPrefix="SearchFiltersDesktop"
                      className={css.filter}
                      config={filterConfig}
                      currentPage="SearchPage"
                      listingCategories={listingCategories}
                      users={filterUsersByListings(users, listings)}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.getEnrichedProps(), this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                      isDesktop
                    />
                  );
                })}
                <SecondaryButton
                  className={css.resetAllButton}
                  onClick={e => this.handleResetAll(e)}
                >
                  <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
                </SecondaryButton>
              </div>
            </aside>

            <div className={css.layoutWrapperMain} role="main">
              <div className={css.searchResultContainer}>
                <SearchFiltersMobile
                  className={css.searchFiltersMobileList}
                  urlQueryParams={validQueryParams}
                  sortByComponent={sortBy('mobile')}
                  resultsAreLoaded={listingsAreLoaded}
                  resultsCount={totalItems}
                  searchInProgress={searchInProgress}
                  searchListingsError={searchListingsError}
                  showAsModalMaxWidth={MODAL_BREAKPOINT}
                  onManageDisableScrolling={onManageDisableScrolling}
                  onOpenModal={this.onOpenMobileModal}
                  onCloseModal={this.onCloseMobileModal}
                  resetAll={this.resetAll}
                  selectedFiltersCount={selectedFiltersCountForMobile}
                  isMapVariant={false}
                >
                  {sortedAvailableFilters.map(filterConfig => {
                    const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${filterConfig.key
                      }`;

                    return (
                      <FilterComponent
                        key={key}
                        idPrefix="SearchFiltersMobile"
                        config={filterConfig}
                        currentPage="ProfilePage"
                        listingCategories={listingCategories}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        initialValues={initialValues(this.getEnrichedProps(), this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        liveEdit
                        showAsPopup={false}
                      />
                    );
                  })}
                </SearchFiltersMobile>
                <div
                  className={classNames(css.listingsForGridVariant, {
                    [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                  })}
                >
                  {noResultsInfo}
                  <SearchResultsPanel
                    className={css.searchListingsPanel}
                    listings={listings}
                    pagination={listingsAreLoaded ? pagination : null}
                    search={parse(location.search)}
                    isMapVariant={false}
                    onAddToCart={onAddToCart}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <FooterContainer />
      </Page>
    );
  }
}

SearchPageComponent.defaultProps = {
  listings: [],
  marketplaceListings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
  availableBrands: null,
};

SearchPageComponent.propTypes = {
  listings: array,
  marketplaceListings: array,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,
  queryUsersInProgress: bool.isRequired,
  queryUsersError: propTypes.error,
  users: arrayOf(propTypes.user).isRequired,
  availableBrands: array,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    marketplaceListings,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    queryUsersInProgress,
    queryUsersError,
    userIds,
    catalogPageResultIds,
    catalogPagination,
    catalogSearchInProgress,
    mergedPageResultIds,
    mergedPagination,
    availableBrands,
  } = state.SearchPage;

  // mergedPageResultIds is non-null only while a cross-source sort (price/
  // date) is active — it already holds one correctly interleaved id list for
  // this page. Otherwise fall back to the per-source block concatenation.
  const isMergedSort = mergedPageResultIds !== null;
  const listings = isMergedSort
    ? getListingsById(state, mergedPageResultIds)
    : getListingsById(state, [...currentPageResultIds, ...catalogPageResultIds]);
  const users = getUsersById(state, userIds);

  // The two sources resolve at different times (separate requests). Treating
  // the page as "loaded" as soon as the Sharetribe half resolves would render
  // new Sharetribe listings next to the PREVIOUS page's still-stale catalog
  // listings for a moment, so isSearchInProgress covers both.
  const isSearchInProgress = searchInProgress || catalogSearchInProgress;

  // Sharetribe listings and Supabase catalog products are queried separately
  // (same page/perPage) and merged into one grid here. Totals are combined so
  // pagination controls keep working until both sources are exhausted; page
  // and perPage are taken from the Sharetribe response since that's what
  // drives the URL's page param.
  const blockPagination = pagination
    ? {
        ...pagination,
        // Sharetribe's own paginationLimit only reflects its own (smaller)
        // result set — PaginationLinks prioritizes it over totalPages when
        // present, which would wrongly cap navigation below the merged total.
        paginationLimit: undefined,
        totalItems: (pagination.totalItems || 0) + (catalogPagination?.totalItems || 0),
        totalPages: Math.max(pagination.totalPages || 0, catalogPagination?.totalPages || 0),
      }
    : pagination;

  return {
    currentUser,
    listings,
    marketplaceListings,
    pagination: isMergedSort ? mergedPagination : blockPagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress: isSearchInProgress,
    searchListingsError,
    searchParams,
    queryUsersInProgress,
    queryUsersError,
    users,
    availableBrands,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onAddToCart: (listingId, quantity, deliveryMethod) =>
    dispatch(addToCart(listingId, quantity, deliveryMethod)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedSearchPage);

export default SearchPage;
